import {
  Div,
  ListSpinner,
  ModalPortal,
  P,
  TableHeader,
  lightTheme,
} from '@vgrubs/components';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { GAMES_COLUMNS, GamesPlayerRow } from './GamesPlayerRow';
import { GamesRange } from './gamesFormat';
import { useGamesPlayers } from './useGamesPlayers';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  range: GamesRange;
  periodLabel?: string;
};

/**
 * Every player of the range, biggest MyChips payment first, the next page
 * loading as the spinner at the bottom comes into view. It reads the same
 * cache as the card, so the first page is already there.
 */
export const GamesPlayersModal = ({
  isOpen,
  onClose,
  range,
  periodLabel,
}: Props) => {
  const {
    players,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGamesPlayers(range);
  const [sentinelRef, sentinelInView] = useInView();

  useEffect(() => {
    if (isOpen && sentinelInView && hasNextPage && !isFetchingNextPage)
      fetchNextPage();
  }, [isOpen, sentinelInView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={onClose}
      backgroundColor={lightTheme.white}
      padding="24px"
      logo={false}
      width="1200px"
      maxHeight="80vh"
      modalOverflow="auto"
      titleWithExit="Play & Earn Players"
      subtitle={periodLabel}
    >
      <TableHeader columns={GAMES_COLUMNS} padding="0 16px" gap="12px" />

      {players.map((player) => (
        <GamesPlayerRow key={player.gamesUserId} player={player} />
      ))}

      {isError && (
        <Div $flex $center $height="100px">
          <P $m="0" $color={lightTheme.primary}>
            The players could not be loaded.
          </P>
        </Div>
      )}

      <ListSpinner
        loading={isLoading || isFetchingNextPage}
        observerRef={sentinelRef}
        mb="0"
        mbSM="0"
      />
    </ModalPortal>
  );
};
